// src/shared/lib/custom-list-step.domain.ts
// Pure helpers for custom-list SOP step config and report resolution.

export type CustomListOption = {
  label: string;
  reportText: string;
};

export type CustomListConfig = {
  options?: CustomListOption[];
  allowMultiple?: boolean;
};

export type CustomListReportContext = {
  prompt?: string;
  title?: string;
};

export type CustomListValidationIssue = {
  severity: 'block' | 'warn';
  code: 'min_options' | 'empty_label' | 'duplicate_label' | 'empty_report_output';
  message: string;
  optionIndex?: number;
};

/** Placeholders substituted in per-option custom-list report sentences. */
export const CUSTOM_LIST_REPORT_PLACEHOLDER_TOKENS = ['{prompt}', '{title}'] as const;

function dedupeCustomListOptions(options: CustomListOption[]): CustomListOption[] {
  const seen = new Set<string>();
  const out: CustomListOption[] = [];
  for (const option of options) {
    const label = String(option.label || '').trim();
    if (!label) continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      label,
      reportText: String(option.reportText || '').trim(),
    });
  }
  return out;
}

function legacyReportTextForLabel(legacyReportActionText: string | null | undefined, label: string): string {
  const template = String(legacyReportActionText || '').trim();
  if (!template) return label;
  if (template.includes('{selections}')) {
    return template.replace(/{selections}/g, label);
  }
  return template;
}

function normalizeRawOption(
  raw: unknown,
  legacyReportActionText?: string | null,
): CustomListOption | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const label = raw.trim();
    if (!label) return null;
    return {
      label,
      reportText: legacyReportTextForLabel(legacyReportActionText, label),
    };
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const label = String(obj.label ?? obj.value ?? '').trim();
    if (!label) return null;
    if (Object.prototype.hasOwnProperty.call(obj, 'reportText') || Object.prototype.hasOwnProperty.call(obj, 'report_text')) {
      const explicitRaw = obj.reportText ?? obj.report_text;
      return {
        label,
        reportText: explicitRaw == null ? '' : String(explicitRaw).trim(),
      };
    }
    return {
      label,
      reportText: legacyReportTextForLabel(legacyReportActionText, label),
    };
  }
  const label = String(raw).trim();
  if (!label || label === '[object Object]') return null;
  return {
    label,
    reportText: legacyReportTextForLabel(legacyReportActionText, label),
  };
}

export function parseCustomListConfig(
  raw: unknown,
  legacyReportActionText?: string | null,
): CustomListConfig {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return { options: [], allowMultiple: true };
    }
  }
  if (source == null || typeof source !== 'object') {
    return { options: [], allowMultiple: true };
  }
  const obj = source as Record<string, unknown>;
  const rawOptions = Array.isArray(obj.options) ? obj.options : [];
  const options = dedupeCustomListOptions(
    rawOptions
      .map((entry) => normalizeRawOption(entry, legacyReportActionText))
      .filter((entry): entry is CustomListOption => entry != null),
  );
  return {
    options,
    allowMultiple: obj.allowMultiple !== false,
  };
}

/**
 * Editor parser: preserve empty, duplicate, and whitespace-only rows while the
 * author is typing. Runtime parsing remains normalized and deduplicated.
 */
export function parseCustomListConfigForEdit(
  raw: unknown,
  legacyReportActionText?: string | null,
): CustomListConfig {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return { options: [], allowMultiple: true };
    }
  }
  if (source == null || typeof source !== 'object') {
    return { options: [], allowMultiple: true };
  }
  const obj = source as Record<string, unknown>;
  const rawOptions = Array.isArray(obj.options) ? obj.options : [];
  const options = rawOptions.map((rawOption) => {
    if (typeof rawOption === 'string') {
      return {
        label: rawOption,
        reportText: legacyReportTextForLabel(legacyReportActionText, rawOption),
      };
    }
    if (rawOption != null && typeof rawOption === 'object') {
      const option = rawOption as Record<string, unknown>;
      const label = String(option.label ?? option.value ?? '');
      const hasExplicitReportText =
        Object.prototype.hasOwnProperty.call(option, 'reportText')
        || Object.prototype.hasOwnProperty.call(option, 'report_text');
      return {
        label,
        reportText: hasExplicitReportText
          ? String(option.reportText ?? option.report_text ?? '')
          : legacyReportTextForLabel(legacyReportActionText, label),
      };
    }
    return { label: String(rawOption ?? ''), reportText: '' };
  });
  return {
    options,
    allowMultiple: obj.allowMultiple !== false,
  };
}

export function customListValidationIssues(
  config: unknown,
  options?: { includeInReport?: boolean; legacyReportActionText?: string | null },
): CustomListValidationIssue[] {
  const parsed = parseCustomListConfigForEdit(config, options?.legacyReportActionText);
  const rows = parsed.options ?? [];
  const issues: CustomListValidationIssue[] = [];
  const seenLabels = new Map<string, number>();

  if (rows.length < 1) {
    issues.push({
      severity: 'block',
      code: 'min_options',
      message: 'Add at least one list option.',
    });
  }

  rows.forEach((row, index) => {
    const label = String(row.label || '').trim();
    if (!label) {
      issues.push({
        severity: 'block',
        code: 'empty_label',
        message: `List option ${index + 1} needs an operator label.`,
        optionIndex: index,
      });
      return;
    }
    const normalized = label.toLocaleLowerCase();
    const priorIndex = seenLabels.get(normalized);
    if (priorIndex != null) {
      issues.push({
        severity: 'block',
        code: 'duplicate_label',
        message: `List option ${index + 1} duplicates option ${priorIndex + 1} ("${label}"). Use a unique operator label.`,
        optionIndex: index,
      });
    } else {
      seenLabels.set(normalized, index);
    }
    if (options?.includeInReport !== false && !String(row.reportText || '').trim()) {
      issues.push({
        severity: 'warn',
        code: 'empty_report_output',
        message: `List option ${index + 1} ("${label}") is missing report action text.`,
        optionIndex: index,
      });
    }
  });

  return issues;
}

export function getCustomListOptionLabels(config: unknown, legacyReportActionText?: string | null): string[] {
  return parseCustomListConfig(config, legacyReportActionText).options?.map((option) => option.label) ?? [];
}

export function isCustomListStep(archetype: unknown, variant: unknown): boolean {
  return String(archetype || '').toUpperCase() === 'SPECIAL_ACTION'
    && String(variant || '').toLowerCase() === 'custom_list';
}

export function extractCustomListSelections(entry: Record<string, unknown> | null | undefined): string[] {
  const raw = entry?.selectedOptions ?? entry?.customListSelections
    ?? (Array.isArray(entry?.selections) ? entry.selections : []);
  return Array.isArray(raw) ? raw.map((value) => String(value).trim()).filter(Boolean) : [];
}

function substituteCustomListPlaceholders(text: string, context: CustomListReportContext): string {
  const prompt = String(context.prompt || '').replace(/\?+$/, '').trim();
  const title = String(context.title || '').trim();
  return text
    .replace(/{prompt}/g, prompt)
    .replace(/{title}/g, title);
}

function findCustomListOption(
  options: CustomListOption[],
  label: string,
): CustomListOption | undefined {
  const trimmed = label.trim();
  if (!trimmed) return undefined;
  const exact = options.find((option) => option.label === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLocaleLowerCase();
  return options.find((option) => option.label.toLocaleLowerCase() === lower);
}

export function resolveCustomListReportLines(
  selections: string[],
  config: unknown,
  context: CustomListReportContext,
  legacyReportActionText?: string | null,
): string[] {
  const parsed = parseCustomListConfig(config, legacyReportActionText);
  const options = parsed.options ?? [];
  const lines: string[] = [];
  for (const selection of selections) {
    const option = findCustomListOption(options, selection);
    if (!option) continue;
    const reportText = String(option.reportText || '').trim();
    if (!reportText) continue;
    lines.push(substituteCustomListPlaceholders(reportText, context));
  }
  return lines;
}

export function customListOptionsMissingReportText(
  config: unknown,
  legacyReportActionText?: string | null,
): CustomListOption[] {
  const parsed = parseCustomListConfig(config, legacyReportActionText);
  return (parsed.options ?? []).filter((option) => !String(option.reportText || '').trim());
}
